// src/suggestions.ts
function decideSuggestion(input) {
  const { suggestion, actorRole, decision } = input;
  if (suggestion.status !== "pending") {
    return { allowed: false, reason: `suggestion already ${suggestion.status}` };
  }
  if (actorRole === "lost") {
    return { allowed: false, reason: "actor removed from deal" };
  }
  if (decision === "accept" && input.actorId && suggestion.authorId && String(input.actorId) === String(suggestion.authorId)) {
    return { allowed: false, reason: "cannot accept your own suggestion" };
  }
  const nextStatus = decision === "accept" ? "accepted" : "rejected";
  return {
    allowed: true,
    nextStatus,
    reason: suggestion.authorRole === "lost" ? `${decision} of removed-author suggestion` : `${decision}`
  };
}
function hasPendingSuggestions(suggestions) {
  return suggestions.some((s) => s.status === "pending");
}
function pendingCount(suggestions) {
  return suggestions.filter((s) => s.status === "pending").length;
}
function toIndexRow(s, documentId) {
  return { externalId: s.externalId, documentId, authorId: s.authorId, authorRole: s.authorRole, kind: s.kind, status: s.status };
}

// src/comments.ts
function parseMentions(text, directory) {
  const byHandle = new Map(directory.map((u) => [u.handle.toLowerCase(), u]));
  const mentions = [];
  const re = /@(?:"([^"]+)"|([\w.\-]+))/g;
  let m;
  while (m = re.exec(text)) {
    const handle = (m[1] ?? m[2] ?? "").toLowerCase();
    const user = byHandle.get(handle);
    if (user) mentions.push({ userId: user.userId, display: user.display, from: m.index, to: m.index + m[0].length });
  }
  return mentions;
}
function newlyMentioned(before, after) {
  const had = new Set(before.map((x) => x.userId));
  return [...new Set(after.filter((x) => !had.has(x.userId)).map((x) => x.userId))];
}
function canViewComment(c, viewer) {
  return c.isPublic || c.authorTeamId === viewer.teamId;
}
function viewComment(c, viewer) {
  if (canViewComment(c, viewer)) return c;
  return { ...c, text: "[PRIVATE]", mentions: [] };
}
function commentToIndexRow(c, documentId) {
  return { externalId: c.externalId, documentId, threadId: c.threadId, authorId: c.authorId, authorTeamId: c.authorTeamId, isPublic: c.isPublic, resolvedAt: c.resolvedAt };
}
function unresolvedCounts(rows, viewer) {
  const visible = rows.filter((r) => (r.isPublic || r.authorTeamId === viewer.teamId) && !r.resolvedAt);
  const byThread = {};
  for (const r of visible) byThread[r.threadId] = (byThread[r.threadId] ?? 0) + 1;
  return { total: visible.length, byThread };
}

// src/sanitizer.ts
var PRIVATE_COMMENT_BOOKMARK_PREFIX = "propdocs-pc-";
var BOOKMARK_ID_BASE = 9e5;
function nextFreeBookmarkId(documentXml) {
  let max = BOOKMARK_ID_BASE - 1;
  const re = /<w:bookmark(?:Start|End)\b[^>]*\bw:id="(\d+)"/g;
  let m;
  while ((m = re.exec(documentXml)) !== null) {
    const value = Number(m[1]);
    if (Number.isFinite(value) && value > max) max = value;
  }
  return max + 1;
}
function sanitizeExport(input) {
  const privateSet = new Set(input.privateCommentIds);
  let removedComments = 0;
  let removedAnchors = 0;
  let commentsXml = input.commentsXml;
  if (commentsXml) {
    commentsXml = commentsXml.replace(
      /<w:comment\b[^>]*\bw:id="([^"]*)"[\s\S]*?<\/w:comment>/g,
      (whole, id) => {
        if (privateSet.has(id)) {
          removedComments++;
          return "";
        }
        return whole;
      }
    );
  }
  const mode = input.mode ?? "export";
  let documentXml = input.documentXml;
  let bookmarkSeq = nextFreeBookmarkId(documentXml);
  for (const id of privateSet) {
    const bookmarkId = bookmarkSeq++;
    const bookmarkName = `${PRIVATE_COMMENT_BOOKMARK_PREFIX}${id}`;
    documentXml = documentXml.replace(
      new RegExp(`<w:commentRangeStart\\b[^>]*\\bw:id="${escapeRe(id)}"[^>]*/>`, "g"),
      () => {
        removedAnchors++;
        return mode === "persist" ? `<w:bookmarkStart w:id="${bookmarkId}" w:name="${escapeXmlAttr(bookmarkName)}"/>` : "";
      }
    ).replace(
      new RegExp(`<w:commentRangeEnd\\b[^>]*\\bw:id="${escapeRe(id)}"[^>]*/>`, "g"),
      () => {
        removedAnchors++;
        return mode === "persist" ? `<w:bookmarkEnd w:id="${bookmarkId}"/>` : "";
      }
    ).replace(
      new RegExp(
        `<w:r\\b[^>]*>(?:(?!</w:r>)[\\s\\S])*?<w:commentReference\\b[^>]*\\bw:id="${escapeRe(id)}"[^>]*/>[\\s\\S]*?</w:r>`,
        "g"
      ),
      () => {
        removedAnchors++;
        return "";
      }
    );
  }
  if (mode === "export") {
    documentXml = stripPrivateCommentBookmarks(documentXml);
  }
  return { commentsXml, documentXml, removedComments, removedAnchors };
}
function stripPrivateCommentBookmarks(documentXml) {
  const ids = [];
  const startPattern = new RegExp(
    `<w:bookmarkStart\\b[^>]*\\bw:name="${escapeRe(PRIVATE_COMMENT_BOOKMARK_PREFIX)}[^"]*"[^>]*/>`,
    "g"
  );
  let out = documentXml.replace(startPattern, (whole) => {
    const idMatch = /\bw:id="([^"]*)"/.exec(whole);
    if (idMatch) ids.push(idMatch[1]);
    return "";
  });
  for (const id of ids) {
    out = out.replace(new RegExp(`<w:bookmarkEnd\\b[^>]*\\bw:id="${escapeRe(id)}"[^>]*/>`, "g"), "");
  }
  return out;
}
function escapeXmlAttr(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function assertNoPrivateLeak(result, privateIds) {
  for (const id of privateIds) {
    const inComments = result.commentsXml?.includes(`w:id="${id}"`) && /<w:comment\b/.test(result.commentsXml ?? "");
    const inDoc = new RegExp(`w:(commentRangeStart|commentRangeEnd|commentReference)\\b[^>]*\\bw:id="${escapeRe(id)}"`).test(result.documentXml);
    if (inComments || inDoc) throw new Error(`PRIVATE COMMENT LEAK: id ${id} survived export sanitization`);
  }
}

// src/sanitizeExportDocx.ts
import JSZip from "jszip";
import { updateMultipleFiles } from "@propdocs-editor/core/docx";
var COMMENTS_PATH = "word/comments.xml";
var DOCUMENT_PATH = "word/document.xml";
async function sanitizeExportDocx(bytes, privateCommentIds, mode = "export") {
  if (privateCommentIds.length === 0 && mode === "persist") return bytes;
  const zip = await JSZip.loadAsync(bytes);
  const commentsFile = zip.file(COMMENTS_PATH);
  const documentFile = zip.file(DOCUMENT_PATH);
  if (!documentFile) return bytes;
  const commentsXml = commentsFile ? await commentsFile.async("text") : null;
  const documentXml = await documentFile.async("text");
  const result = sanitizeExport({ commentsXml, documentXml, privateCommentIds, mode });
  if (result.documentXml === documentXml && result.commentsXml === commentsXml) return bytes;
  assertNoPrivateLeak(result, privateCommentIds);
  const updates = /* @__PURE__ */ new Map();
  updates.set(DOCUMENT_PATH, result.documentXml);
  if (commentsXml !== null && result.commentsXml !== null) {
    updates.set(COMMENTS_PATH, result.commentsXml);
  }
  return updateMultipleFiles(bytes, updates);
}

// src/rehydratePrivateComments.ts
import JSZip2 from "jszip";
import { updateMultipleFiles as updateMultipleFiles2 } from "@propdocs-editor/core/docx";
var COMMENTS_PATH2 = "word/comments.xml";
var DOCUMENT_PATH2 = "word/document.xml";
function escapeXml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function textToRuns(text) {
  const lines = text.split(/\r?\n/);
  return lines.map((line, i) => {
    const br = i > 0 ? "<w:br/>" : "";
    return `<w:r><w:rPr><w:rStyle w:val="CommentReference"/></w:rPr>${br}<w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r>`;
  }).join("");
}
function buildCommentDefinition(c) {
  const id = escapeXml(String(c.id));
  const author = escapeXml(c.author ?? "PropDocs");
  const initials = escapeXml(c.initials ?? "");
  const date = escapeXml(c.date ?? "1970-01-01T00:00:00Z");
  const parent = c.parentId != null ? ` w16cid:parentId="${escapeXml(String(c.parentId))}"` : "";
  return `<w:comment w:id="${id}" w:author="${author}" w:date="${date}" w:initials="${initials}"${parent}><w:p>${textToRuns(c.text ?? "")}</w:p></w:comment>`;
}
function rangeEndMarkup(commentId) {
  return `<w:commentRangeEnd w:id="${escapeXml(commentId)}"/><w:r><w:rPr><w:rStyle w:val="CommentReference"/></w:rPr><w:commentReference w:id="${escapeXml(commentId)}"/></w:r>`;
}
function escapeRe2(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function rehydratePrivateComments(documentXml, commentsXml, comments) {
  const byId = new Map(comments.map((c) => [String(c.id), c]));
  if (byId.size === 0) return { documentXml, commentsXml, restored: 0 };
  const edits = [];
  const restoredIds = /* @__PURE__ */ new Set();
  const anchorEdits = /* @__PURE__ */ new Map();
  const startPattern = new RegExp(
    `<w:bookmarkStart\\b[^>]*\\bw:name="${escapeRe2(PRIVATE_COMMENT_BOOKMARK_PREFIX)}([^"]*)"[^>]*/>`,
    "g"
  );
  let match;
  while ((match = startPattern.exec(documentXml)) !== null) {
    const commentId = String(match[1]);
    if (!byId.has(commentId) || restoredIds.has(commentId)) continue;
    const bookmarkId = /\bw:id="([^"]*)"/.exec(match[0])?.[1];
    if (!bookmarkId) continue;
    const endPattern = new RegExp(
      `<w:bookmarkEnd\\b[^>]*\\bw:id="${escapeRe2(bookmarkId)}"[^>]*/>`,
      "g"
    );
    endPattern.lastIndex = match.index + match[0].length;
    const end = endPattern.exec(documentXml);
    if (!end) continue;
    restoredIds.add(commentId);
    const startEdit = {
      index: match.index,
      length: match[0].length,
      text: `<w:commentRangeStart w:id="${escapeXml(commentId)}"/>`
    };
    const endEdit = {
      index: end.index,
      length: end[0].length,
      text: rangeEndMarkup(commentId)
    };
    edits.push(startEdit, endEdit);
    anchorEdits.set(commentId, { start: startEdit, end: endEdit });
  }
  let attached = true;
  while (attached) {
    attached = false;
    for (const c of comments) {
      const id = String(c.id);
      if (restoredIds.has(id) || c.parentId == null) continue;
      const parentAnchor = anchorEdits.get(String(c.parentId));
      if (!parentAnchor) continue;
      parentAnchor.start.text += `<w:commentRangeStart w:id="${escapeXml(id)}"/>`;
      parentAnchor.end.text += rangeEndMarkup(id);
      restoredIds.add(id);
      anchorEdits.set(id, parentAnchor);
      attached = true;
    }
  }
  const restored = restoredIds.size;
  if (restored === 0) return { documentXml, commentsXml, restored: 0 };
  edits.sort((a, b) => b.index - a.index);
  let outDoc = documentXml;
  for (const edit of edits) {
    outDoc = outDoc.slice(0, edit.index) + edit.text + outDoc.slice(edit.index + edit.length);
  }
  const definitions = [...restoredIds].map((id) => byId.get(id)).filter((c) => Boolean(c)).map(buildCommentDefinition).join("");
  let outComments = commentsXml;
  if (outComments) {
    outComments = outComments.replace(/<\/w:comments>\s*$/, `${definitions}</w:comments>`);
  }
  return { documentXml: outDoc, commentsXml: outComments, restored };
}
async function rehydratePrivateCommentsDocx(bytes, comments) {
  if (!comments.length) return bytes;
  const zip = await JSZip2.loadAsync(bytes);
  const documentFile = zip.file(DOCUMENT_PATH2);
  if (!documentFile) return bytes;
  const documentXml = await documentFile.async("text");
  if (!documentXml.includes(PRIVATE_COMMENT_BOOKMARK_PREFIX)) return bytes;
  const commentsFile = zip.file(COMMENTS_PATH2);
  const commentsXml = commentsFile ? await commentsFile.async("text") : null;
  const result = rehydratePrivateComments(documentXml, commentsXml, comments);
  if (result.restored === 0) return bytes;
  const updates = /* @__PURE__ */ new Map();
  updates.set(DOCUMENT_PATH2, result.documentXml);
  if (commentsXml !== null && result.commentsXml !== null) {
    updates.set(COMMENTS_PATH2, result.commentsXml);
  }
  return updateMultipleFiles2(bytes, updates);
}
export {
  PRIVATE_COMMENT_BOOKMARK_PREFIX,
  assertNoPrivateLeak,
  canViewComment,
  commentToIndexRow,
  decideSuggestion,
  escapeXml,
  hasPendingSuggestions,
  newlyMentioned,
  parseMentions,
  pendingCount,
  rehydratePrivateComments,
  rehydratePrivateCommentsDocx,
  sanitizeExport,
  sanitizeExportDocx,
  stripPrivateCommentBookmarks,
  toIndexRow,
  unresolvedCounts,
  viewComment
};
